import React, { useState, useEffect } from "react";
import axios from "axios";
import { format } from "date-fns";
import { es as esLocale, enUS } from "date-fns/locale";
import { CalendarIcon, Clock, Send, Loader2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Calendar } from "../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Toaster, toast } from "sonner";
import { useI18n } from "../i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIME_SLOTS = [
  "09:00", "09:30", "10:00", "10:30", "11:00", "11:30",
  "12:00", "12:30", "13:00", "16:00", "16:30", "17:00", "17:30", "18:00",
];

const fmtDate = (d) => (d ? format(d, "yyyy-MM-dd") : "");

const ContactDemo = () => {
  const { t, lang } = useI18n();
  const locale = lang === "es" ? esLocale : enUS;

  // Demo form state
  const [date, setDate] = useState(null);
  const [time, setTime] = useState("");
  const [unavailable, setUnavailable] = useState([]);
  const [demoForm, setDemoForm] = useState({
    name: "", email: "", phone: "", clinic: "", notes: "",
  });
  const [submittingDemo, setSubmittingDemo] = useState(false);

  // Contact form state
  const [contactForm, setContactForm] = useState({
    name: "", email: "", phone: "", clinic: "", message: "",
  });
  const [submittingContact, setSubmittingContact] = useState(false);

  // Disable past dates and weekends (Sat=6, Sun=0)
  const disabledDays = (d) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (d < today) return true;
    const day = d.getDay();
    return day === 0 || day === 6;
  };

  useEffect(() => {
    const loadAvailability = async () => {
      if (!date) return setUnavailable([]);
      try {
        const r = await axios.get(`${API}/demo/availability`, {
          params: { date: fmtDate(date) },
        });
        setUnavailable(r.data?.unavailable || []);
      } catch (e) {
        setUnavailable([]);
      }
    };
    loadAvailability();
  }, [date]);

  const handleDemoSubmit = async (e) => {
    e.preventDefault();
    if (!date || !time) {
      toast.error(t.contact.form.selectDate);
      return;
    }
    setSubmittingDemo(true);
    try {
      await axios.post(`${API}/demo`, {
        ...demoForm,
        date: fmtDate(date),
        time,
      });
      toast.success(t.contact.form.successBook);
      setDemoForm({ name: "", email: "", phone: "", clinic: "", notes: "" });
      setDate(null);
      setTime("");
    } catch (err) {
      toast.error(t.contact.form.error);
    } finally {
      setSubmittingDemo(false);
    }
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setSubmittingContact(true);
    try {
      await axios.post(`${API}/contact`, contactForm);
      toast.success(t.contact.form.successContact);
      setContactForm({ name: "", email: "", phone: "", clinic: "", message: "" });
    } catch (err) {
      toast.error(t.contact.form.error);
    } finally {
      setSubmittingContact(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="py-24 md:py-32 bg-white">
      <Toaster position="top-center" richColors />
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16">
          <div className="lg:col-span-5">
            <div className="text-xs font-semibold uppercase tracking-[0.2em] text-sky-600 mb-4">
              {t.contact.eyebrow}
            </div>
            <h2 className="font-display text-3xl md:text-5xl font-semibold text-slate-900 leading-tight">
              {t.contact.title}
            </h2>
            <p className="mt-6 text-base md:text-lg text-slate-600 leading-relaxed">
              {t.contact.subtitle}
            </p>
            <div
              className="mt-10 hidden lg:block rounded-2xl overflow-hidden border border-slate-200 h-72 bg-cover bg-center"
              style={{
                backgroundImage:
                  "url('https://images.pexels.com/photos/4269276/pexels-photo-4269276.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940')",
              }}
            />
          </div>

          <div className="lg:col-span-7">
            <Tabs defaultValue="book" className="w-full">
              <TabsList
                data-testid="contact-tabs"
                className="grid w-full grid-cols-2 bg-slate-100 p-1 h-12 rounded-full"
              >
                <TabsTrigger
                  data-testid="tab-book"
                  value="book"
                  className="rounded-full data-[state=active]:bg-slate-900 data-[state=active]:text-white h-10"
                >
                  {t.contact.tabBook}
                </TabsTrigger>
                <TabsTrigger
                  data-testid="tab-contact"
                  value="contact"
                  className="rounded-full data-[state=active]:bg-slate-900 data-[state=active]:text-white h-10"
                >
                  {t.contact.tabContact}
                </TabsTrigger>
              </TabsList>

              <TabsContent value="book" className="mt-8">
                <form
                  data-testid="demo-form"
                  onSubmit={handleDemoSubmit}
                  className="bg-slate-50 border border-slate-200 rounded-2xl p-6 md:p-8 space-y-5"
                >
                  <div className="grid md:grid-cols-2 gap-5">
                    <div>
                      <Label className="text-slate-700">{t.contact.form.name}</Label>
                      <Input
                        data-testid="demo-name"
                        required
                        value={demoForm.name}
                        onChange={(e) => setDemoForm({ ...demoForm, name: e.target.value })}
                        className="mt-1.5 bg-white"
                      />
                    </div>
                    <div>
                      <Label className="text-slate-700">{t.contact.form.email}</Label>
                      <Input
                        data-testid="demo-email"
                        type="email"
                        required
                        value={demoForm.email}
                        onChange={(e) => setDemoForm({ ...demoForm, email: e.target.value })}
                        className="mt-1.5 bg-white"
                      />
                    </div>
                    <div>
                      <Label className="text-slate-700">{t.contact.form.phone}</Label>
                      <Input
                        data-testid="demo-phone"
                        required
                        value={demoForm.phone}
                        onChange={(e) => setDemoForm({ ...demoForm, phone: e.target.value })}
                        className="mt-1.5 bg-white"
                      />
                    </div>
                    <div>
                      <Label className="text-slate-700">{t.contact.form.clinic}</Label>
                      <Input
                        data-testid="demo-clinic"
                        required
                        value={demoForm.clinic}
                        onChange={(e) => setDemoForm({ ...demoForm, clinic: e.target.value })}
                        className="mt-1.5 bg-white"
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-5">
                    <div>
                      <Label className="text-slate-700">{t.contact.form.date}</Label>
                      <Popover>
                        <PopoverTrigger asChild>
                          <button
                            data-testid="demo-date-trigger"
                            type="button"
                            className="mt-1.5 w-full h-10 bg-white border border-slate-200 rounded-md px-3 text-left text-sm flex items-center justify-between hover:border-slate-300"
                          >
                            <span className={date ? "text-slate-900" : "text-slate-400"}>
                              {date ? format(date, "PPP", { locale }) : t.contact.form.date}
                            </span>
                            <CalendarIcon className="w-4 h-4 text-slate-500" />
                          </button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="start">
                          <Calendar
                            data-testid="demo-calendar"
                            mode="single"
                            selected={date}
                            onSelect={(d) => { setDate(d); setTime(""); }}
                            disabled={disabledDays}
                            locale={locale}
                            initialFocus
                          />
                        </PopoverContent>
                      </Popover>
                    </div>
                    <div>
                      <Label className="text-slate-700">{t.contact.form.time}</Label>
                      <div className="mt-1.5 grid grid-cols-4 gap-1.5">
                        {TIME_SLOTS.map((slot) => {
                          const taken = unavailable.includes(slot);
                          const selected = time === slot;
                          return (
                            <button
                              key={slot}
                              type="button"
                              data-testid={`time-slot-${slot}`}
                              disabled={!date || taken}
                              onClick={() => setTime(slot)}
                              className={`text-xs h-8 rounded-md border transition-all ${
                                selected
                                  ? "bg-slate-900 text-white border-slate-900"
                                  : taken || !date
                                  ? "bg-slate-100 text-slate-400 border-slate-200 cursor-not-allowed"
                                  : "bg-white text-slate-700 border-slate-200 hover:border-slate-400"
                              }`}
                            >
                              {slot}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  </div>

                  <div>
                    <Label className="text-slate-700">{t.contact.form.notes}</Label>
                    <Textarea
                      data-testid="demo-notes"
                      rows={3}
                      value={demoForm.notes}
                      onChange={(e) => setDemoForm({ ...demoForm, notes: e.target.value })}
                      className="mt-1.5 bg-white"
                    />
                  </div>

                  <Button
                    data-testid="demo-submit"
                    type="submit"
                    disabled={submittingDemo}
                    className="w-full bg-slate-900 hover:bg-slate-800 text-white rounded-full h-12 text-base font-medium"
                  >
                    {submittingDemo ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <>
                        <Clock className="w-4 h-4 mr-2" />
                        {t.contact.form.submitBook}
                      </>
                    )}
                  </Button>
                </form>
              </TabsContent>

              <TabsContent value="contact" className="mt-8">
                <form
                  data-testid="contact-form"
                  onSubmit={handleContactSubmit}
                  className="bg-slate-50 border border-slate-200 rounded-2xl p-6 md:p-8 space-y-5"
                >
                  <div className="grid md:grid-cols-2 gap-5">
                    <div>
                      <Label className="text-slate-700">{t.contact.form.name}</Label>
                      <Input
                        data-testid="contact-name"
                        required
                        value={contactForm.name}
                        onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                        className="mt-1.5 bg-white"
                      />
                    </div>
                    <div>
                      <Label className="text-slate-700">{t.contact.form.email}</Label>
                      <Input
                        data-testid="contact-email"
                        type="email"
                        required
                        value={contactForm.email}
                        onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                        className="mt-1.5 bg-white"
                      />
                    </div>
                    <div>
                      <Label className="text-slate-700">{t.contact.form.phone}</Label>
                      <Input
                        data-testid="contact-phone"
                        value={contactForm.phone}
                        onChange={(e) => setContactForm({ ...contactForm, phone: e.target.value })}
                        className="mt-1.5 bg-white"
                      />
                    </div>
                    <div>
                      <Label className="text-slate-700">{t.contact.form.clinic}</Label>
                      <Input
                        data-testid="contact-clinic"
                        value={contactForm.clinic}
                        onChange={(e) => setContactForm({ ...contactForm, clinic: e.target.value })}
                        className="mt-1.5 bg-white"
                      />
                    </div>
                  </div>
                  <div>
                    <Label className="text-slate-700">{t.contact.form.message}</Label>
                    <Textarea
                      data-testid="contact-message"
                      required
                      rows={4}
                      value={contactForm.message}
                      onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                      className="mt-1.5 bg-white"
                    />
                  </div>
                  <Button
                    data-testid="contact-submit"
                    type="submit"
                    disabled={submittingContact}
                    className="w-full bg-slate-900 hover:bg-slate-800 text-white rounded-full h-12 text-base font-medium"
                  >
                    {submittingContact ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <>
                        <Send className="w-4 h-4 mr-2" />
                        {t.contact.form.submitContact}
                      </>
                    )}
                  </Button>
                </form>
              </TabsContent>
            </Tabs>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactDemo;
